import { GlobalVariables } from 'app/services/globals';
import { TabularTypeDataset } from './tabular-type.dataset';
import { stringIsNullOrEmpty } from 'app/shared/utils/string.util';
import { Metamodel, TabularCopySource } from 'shell-models-lib';
import { HubSpotObjectDataset } from './hub-spot.dataset';
import { HubSpotLinkedService } from '../linkedservice/hub-spot.linkedservice';
import { IDatasetTypeMetadata } from 'app/extensibility/metadatas/resource-type/dataset-type-metadata';

export function getLabelFromPath(path: string): string {
    return stringIsNullOrEmpty(path) ? '' : decodeURIComponent(path.replace('/', '.'));
}

export function separateTableSchemaFromPath(path: string, separator?: string, dataset?: TabularTypeDataset, datasetConfig?: IDatasetTypeMetadata): string[] { // return [schema, table]
    if (path == null) {
        return [];
    }
    
    // Check if this is HubSpot v2 with split path enabled
    const isHubSpotV2WithSplitPath = dataset instanceof HubSpotObjectDataset && 
        (dataset.linkedService as HubSpotLinkedService)?.version === 'v2' &&
        GlobalVariables.enableTableNameSplitForHubspotV2;
    
    let result: string[];
    
    if (isHubSpotV2WithSplitPath) {
        // For HubSpot v2, fetch separator from dataset config
        const hubSpotSeparator = datasetConfig?.separator;
        // New logic: split only at last separator for HubSpot v2
        const lastSeparatorIndex = path.lastIndexOf(hubSpotSeparator);
        if (lastSeparatorIndex === -1) {
            result = [undefined, decodeURIComponent(path)];
        } else {
            const schema = path.substring(0, lastSeparatorIndex);
            const table = path.substring(lastSeparatorIndex + hubSpotSeparator.length);
            result = [decodeURIComponent(schema), decodeURIComponent(table)];
        }
    } else {
        // Original logic: split at all separators for other datasets
        result = separator ? path.split(separator).map((p) => decodeURIComponent(p)) : path.split('/').map((p) => decodeURIComponent(p));
        while (result.length < 2) { // if the path can't be split, we should put it in table and leave schema empty
            result.unshift(undefined);
        }
    }

    // @ is a special char in logicApp. If schema/table starts with @, it will be treated as expression. Both copy and schema API will fail
    // Let's only handle two segments pattern for now. 
    if (GlobalVariables.enableTableNameEscape) {
        if (result.length === 2) {
            if (result[0]?.startsWith('@')) {
                result[0] = '@' + result[0];
            }
            if (result[1]?.startsWith('@')) {
                result[1] = '@' + result[1];
            }
        }
    }

    return result;
}


export function combineSchemaTableToPath(parts: string[], datasetConfig?: IDatasetTypeMetadata): string {
    const separator = datasetConfig?.separator ?? '/';
    return parts.filter((part) => part != null).map((p) => encodeURIComponent(p || '')).join(separator);
}

export function hasTableNameOrTable(dataset: TabularTypeDataset): boolean {
    return !(stringIsNullOrEmpty(dataset.tableName) && stringIsNullOrEmpty(TabularTypeDataset.TABLE_NAME.templateValue(dataset))
        && stringIsNullOrEmpty(dataset.table) && stringIsNullOrEmpty(TabularTypeDataset.TABLE.templateValue(dataset)));
}

export function missingQuery(source: TabularCopySource, dataset: TabularTypeDataset): boolean {
    return stringIsNullOrEmpty((source as TabularCopySource).readerQuery) && stringIsNullOrEmpty(TabularCopySource.READER_QUERY.templateValue(source));
}

// if a TabularTypeDataset specifies disableTableName, it means this dataset only supports query and there's no attribute named 'tableName' or 'schema'/'table' in dataset model
// Dynamic connector may easily fall into this scenario.
export function getFullTableName(dataset: TabularTypeDataset): string {
    const datasetModel = Metamodel.modelOf(dataset);
    const templateTableName = datasetModel.hasAttribute('tableName') ? datasetModel.attribute('tableName').templateValue(dataset) : undefined;
    const tableName: string = datasetModel.hasAttribute('tableName') ? datasetModel.attribute('tableName').value(dataset) : undefined;
    if (!stringIsNullOrEmpty(templateTableName)) {
        return templateTableName;
    } else if (!stringIsNullOrEmpty(tableName)) {
        return tableName;
    } else {
        let schema;
        let table;
        if (datasetModel.hasAttribute('schema')) {
            schema = datasetModel.attribute('schema').templateValue(dataset) || datasetModel.attribute('schema').value(dataset);
        }
        if (datasetModel.hasAttribute('table')) {
            table = datasetModel.attribute('table').templateValue(dataset) || datasetModel.attribute('table').value(dataset);
        }
        return [schema, table].filter((n) => n).join('.');
    }
}
